import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { 
  ToggleLeft, 
  Home, 
  Users, 
  Settings,
  Check,
  X,
  Building2,
  Plus,
  Trash2,
  Palette,
  Ticket,
  Grid3X3,
  Shield,
  Eye,
  Edit,
  Crown
} from 'lucide-react';

const FEATURE_ICONS = {
  intranet: Home,
  tischplan: Users,
  tickets: Ticket,
  nutzerverwaltung: Settings
};

const ROLE_LABELS = {
  super_admin: { label: 'Super Admin', icon: Crown, color: 'text-yellow-500' },
  admin: { label: 'Admin', icon: Shield, color: 'text-purple-500' },
  feature_admin: { label: 'Feature Admin', icon: Settings, color: 'text-blue-500' },
  hostess: { label: 'Hostess', icon: Users, color: 'text-pink-500' },
  basic: { label: 'Basic', icon: Eye, color: 'text-gray-500' }
};

const PERMISSION_LEVELS = [
  { value: 'none', label: '-', color: 'bg-gray-200 dark:bg-gray-700' },
  { value: 'view', label: 'Lesen', color: 'bg-blue-500' },
  { value: 'edit', label: 'Bearbeiten', color: 'bg-green-500' },
  { value: 'admin', label: 'Admin', color: 'bg-purple-500' }
];

const PRESET_COLORS = [
  '#e30613', '#000000', '#3b82f6', '#22c55e', 
  '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4'
];

const AdminFeatures = () => {
  const { theme } = useAuth();
  const [features, setFeatures] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('features');
  
  // Dialog states
  const [showDeptDialog, setShowDeptDialog] = useState(false);
  const [newDeptName, setNewDeptName] = useState('');
  const [newDeptColor, setNewDeptColor] = useState('#e30613');
  const [editingDept, setEditingDept] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/admin/features');
      setFeatures(res.data.features || []);
      setDepartments(res.data.departments || []);
      setRoles(res.data.roles || []);
    } catch (error) {
      console.error('Error fetching features:', error);
      toast.error('Fehler beim Laden der Features');
    } finally {
      setLoading(false);
    }
  };

  // Toggle feature active/inactive
  const toggleFeature = async (featureId, currentState) => {
    try {
      await axios.patch(`/admin/features/${featureId}`, {
        is_active: !currentState
      });
      toast.success(`Feature ${!currentState ? 'aktiviert' : 'deaktiviert'}`);
      fetchData();
    } catch (error) {
      toast.error('Fehler beim Aktualisieren');
    }
  };

  // Update role permission for a feature
  const updateRolePermission = async (featureId, role, level) => {
    const feature = features.find(f => f.id === featureId);
    if (!feature) return;

    const existingRolePerms = feature.role_permissions || [];
    const newRolePerms = existingRolePerms.filter(rp => rp.role !== role);
    
    if (level !== 'none') {
      newRolePerms.push({ role, level, sub_permissions: [] });
    }

    try {
      await axios.patch(`/admin/features/${featureId}`, {
        role_permissions: newRolePerms
      });
      toast.success('Berechtigung aktualisiert');
      fetchData();
    } catch (error) {
      toast.error('Fehler beim Aktualisieren');
    }
  };

  // Update department permission for a feature
  const updateDeptPermission = async (featureId, deptId, level) => {
    const feature = features.find(f => f.id === featureId);
    if (!feature) return;

    const existingDeptPerms = feature.department_permissions || [];
    const newDeptPerms = existingDeptPerms.filter(dp => dp.department_id !== deptId);
    
    if (level !== 'none') {
      newDeptPerms.push({ department_id: deptId, level, sub_permissions: [] });
    }

    try {
      await axios.patch(`/admin/features/${featureId}`, {
        department_permissions: newDeptPerms
      });
      toast.success('Berechtigung aktualisiert');
      fetchData();
    } catch (error) {
      toast.error('Fehler beim Aktualisieren');
    }
  };

  // Get current permission level for a role on a feature
  const getRolePermissionLevel = (feature, role) => {
    const perm = (feature.role_permissions || []).find(rp => rp.role === role);
    return perm?.level || 'none';
  };

  // Get current permission level for a department on a feature
  const getDeptPermissionLevel = (feature, deptId) => {
    const perm = (feature.department_permissions || []).find(dp => dp.department_id === deptId);
    return perm?.level || 'none';
  };

  // Department CRUD
  const saveDepartment = async () => {
    if (!newDeptName.trim()) {
      toast.error('Bitte Namen eingeben');
      return;
    }

    try {
      if (editingDept) {
        await axios.patch(`/admin/departments/${editingDept.id}`, {
          name: newDeptName,
          color: newDeptColor
        });
        toast.success('Abteilung aktualisiert');
      } else {
        await axios.post('/admin/departments', {
          name: newDeptName,
          color: newDeptColor
        });
        toast.success('Abteilung erstellt');
      }
      setShowDeptDialog(false);
      setNewDeptName('');
      setNewDeptColor('#e30613');
      setEditingDept(null);
      fetchData();
    } catch (error) {
      toast.error('Fehler beim Speichern');
    }
  };

  const deleteDepartment = async (deptId) => {
    if (!confirm('Abteilung wirklich löschen?')) return;
    
    try {
      await axios.delete(`/admin/departments/${deptId}`);
      toast.success('Abteilung gelöscht');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  const openEditDept = (dept) => {
    setEditingDept(dept);
    setNewDeptName(dept.name);
    setNewDeptColor(dept.color);
    setShowDeptDialog(true);
  };

  // Permission selector component
  const PermissionSelector = ({ value, onChange }) => (
    <div className="flex gap-1">
      {PERMISSION_LEVELS.map((level) => (
        <button
          key={level.value}
          onClick={() => onChange(level.value)}
          className={`w-6 h-6 rounded text-xs font-bold transition-all ${
            value === level.value 
              ? `${level.color} text-white shadow-md scale-110` 
              : 'bg-gray-100 dark:bg-gray-700 text-gray-400 hover:bg-gray-200'
          }`}
          title={level.label}
        >
          {level.value === 'none' ? '-' : level.label[0]}
        </button>
      ))}
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#e30613] border-t-transparent"></div>
      </div>
    );
  }

  // Filter roles for matrix (exclude super_admin and admin as they have full access)
  const matrixRoles = roles.filter(r => !['super_admin', 'admin'].includes(r));

  return (
    <div className="space-y-6" data-testid="admin-features-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <ToggleLeft className="w-6 h-6 text-[#e30613]" />
          Feature-Verwaltung
        </h1>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="features" className="flex items-center gap-2">
            <ToggleLeft className="w-4 h-4" />
            Features
          </TabsTrigger>
          <TabsTrigger value="role-matrix" className="flex items-center gap-2">
            <Grid3X3 className="w-4 h-4" />
            Rollen-Matrix
          </TabsTrigger>
          <TabsTrigger value="dept-matrix" className="flex items-center gap-2">
            <Building2 className="w-4 h-4" />
            Abteilungs-Matrix
          </TabsTrigger>
          <TabsTrigger value="departments" className="flex items-center gap-2">
            <Building2 className="w-4 h-4" />
            Abteilungen
          </TabsTrigger>
        </TabsList>

        {/* Features Tab - Global Toggle */}
        <TabsContent value="features" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Features global aktivieren/deaktivieren</CardTitle>
              <CardDescription>
                Deaktivierte Features sind für niemanden außer Admins sichtbar
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {features.map((feature) => {
                const IconComponent = FEATURE_ICONS[feature.id] || Settings;
                return (
                  <div 
                    key={feature.id}
                    className="flex items-center justify-between p-4 border rounded-lg"
                  >
                    <div className="flex items-center gap-4">
                      <div className={`p-2 rounded-lg ${feature.is_active ? 'bg-green-100 dark:bg-green-900/30' : 'bg-gray-100 dark:bg-gray-800'}`}>
                        <IconComponent className={`w-5 h-5 ${feature.is_active ? 'text-green-600' : 'text-gray-400'}`} />
                      </div>
                      <div>
                        <h3 className="font-semibold">{feature.name}</h3>
                        <p className="text-sm text-gray-500">{feature.description}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <Badge variant={feature.is_active ? 'default' : 'secondary'}>
                        {feature.is_active ? 'Aktiv' : 'Inaktiv'}
                      </Badge>
                      <Switch
                        checked={feature.is_active}
                        onCheckedChange={() => toggleFeature(feature.id, feature.is_active)}
                      />
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Role Matrix Tab */}
        <TabsContent value="role-matrix" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Berechtigungen nach Rolle</CardTitle>
              <CardDescription>
                <span className="flex items-center gap-4 mt-2 text-xs">
                  <span className="flex items-center gap-1"><span className="w-4 h-4 rounded bg-gray-200 dark:bg-gray-700"></span> Keine</span>
                  <span className="flex items-center gap-1"><span className="w-4 h-4 rounded bg-blue-500"></span> Lesen</span>
                  <span className="flex items-center gap-1"><span className="w-4 h-4 rounded bg-green-500"></span> Bearbeiten</span>
                  <span className="flex items-center gap-1"><span className="w-4 h-4 rounded bg-purple-500"></span> Admin</span>
                </span>
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-yellow-600 dark:text-yellow-400 mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                <Crown className="w-4 h-4 inline mr-2" />
                Super Admin und Admin haben automatisch vollen Zugriff auf alle Features
              </p>
              
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-3 font-semibold">Feature</th>
                      {matrixRoles.map(role => (
                        <th key={role} className="text-center p-3 font-semibold">
                          <div className="flex flex-col items-center gap-1">
                            {ROLE_LABELS[role] && React.createElement(ROLE_LABELS[role].icon, { className: `w-4 h-4 ${ROLE_LABELS[role].color}` })}
                            <span className="text-xs">{ROLE_LABELS[role]?.label || role}</span>
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {features.map((feature) => {
                      const IconComponent = FEATURE_ICONS[feature.id] || Settings;
                      return (
                        <tr key={feature.id} className="border-b hover:bg-gray-50 dark:hover:bg-gray-800/50">
                          <td className="p-3">
                            <div className="flex items-center gap-2">
                              <IconComponent className="w-4 h-4 text-gray-500" />
                              <span className="font-medium">{feature.name}</span>
                              {!feature.is_active && (
                                <Badge variant="secondary" className="text-xs">Inaktiv</Badge>
                              )}
                            </div>
                          </td>
                          {matrixRoles.map(role => (
                            <td key={role} className="p-3 text-center">
                              <PermissionSelector
                                value={getRolePermissionLevel(feature, role)}
                                onChange={(level) => updateRolePermission(feature.id, role, level)}
                              />
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Department Matrix Tab */}
        <TabsContent value="dept-matrix" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Berechtigungen nach Abteilung</CardTitle>
              <CardDescription>
                <span className="flex items-center gap-4 mt-2 text-xs">
                  <span className="flex items-center gap-1"><span className="w-4 h-4 rounded bg-gray-200 dark:bg-gray-700"></span> Keine</span>
                  <span className="flex items-center gap-1"><span className="w-4 h-4 rounded bg-blue-500"></span> Lesen</span>
                  <span className="flex items-center gap-1"><span className="w-4 h-4 rounded bg-green-500"></span> Bearbeiten</span>
                  <span className="flex items-center gap-1"><span className="w-4 h-4 rounded bg-purple-500"></span> Admin</span>
                </span>
              </CardDescription>
            </CardHeader>
            <CardContent>
              {departments.length === 0 ? (
                <p className="text-center text-gray-500 py-8">
                  Keine Abteilungen vorhanden. Erstellen Sie zuerst Abteilungen im Tab "Abteilungen".
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-3 font-semibold">Feature</th>
                        {departments.map(dept => (
                          <th key={dept.id} className="text-center p-3 font-semibold">
                            <div className="flex flex-col items-center gap-1">
                              <div 
                                className="w-4 h-4 rounded-full" 
                                style={{ backgroundColor: dept.color }}
                              />
                              <span className="text-xs">{dept.name}</span>
                            </div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {features.map((feature) => {
                        const IconComponent = FEATURE_ICONS[feature.id] || Settings;
                        return (
                          <tr key={feature.id} className="border-b hover:bg-gray-50 dark:hover:bg-gray-800/50">
                            <td className="p-3">
                              <div className="flex items-center gap-2">
                                <IconComponent className="w-4 h-4 text-gray-500" />
                                <span className="font-medium">{feature.name}</span>
                                {!feature.is_active && (
                                  <Badge variant="secondary" className="text-xs">Inaktiv</Badge>
                                )}
                              </div>
                            </td>
                            {departments.map(dept => (
                              <td key={dept.id} className="p-3 text-center">
                                <PermissionSelector
                                  value={getDeptPermissionLevel(feature, dept.id)}
                                  onChange={(level) => updateDeptPermission(feature.id, dept.id, level)}
                                />
                              </td>
                            ))}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Departments Tab */}
        <TabsContent value="departments" className="space-y-4">
          <div className="flex justify-end">
            <Button 
              onClick={() => {
                setEditingDept(null);
                setNewDeptName('');
                setNewDeptColor('#e30613');
                setShowDeptDialog(true);
              }}
              className="bg-[#e30613] hover:bg-[#c00510]"
            >
              <Plus className="w-4 h-4 mr-2" />
              Neue Abteilung
            </Button>
          </div>

          <div className="grid gap-4">
            {departments.length === 0 ? (
              <Card className="text-center py-8">
                <CardContent>
                  <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">Keine Abteilungen vorhanden</p>
                </CardContent>
              </Card>
            ) : (
              departments.map((dept) => (
                <Card key={dept.id}>
                  <CardContent className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-8 h-8 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: dept.color }}
                      >
                        <Building2 className="w-4 h-4 text-white" />
                      </div>
                      <div>
                        <h3 className="font-semibold">{dept.name}</h3>
                        <p className="text-xs text-gray-500">ID: {dept.id}</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => openEditDept(dept)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => deleteDepartment(dept.id)}
                        className="text-red-500 hover:text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Department Dialog */}
      <Dialog open={showDeptDialog} onOpenChange={setShowDeptDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingDept ? 'Abteilung bearbeiten' : 'Neue Abteilung'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Name</Label>
              <Input
                value={newDeptName}
                onChange={(e) => setNewDeptName(e.target.value)}
                placeholder="z.B. Marketing"
              />
            </div>
            <div>
              <Label>Farbe</Label>
              <div className="flex gap-2 mt-2 flex-wrap">
                {PRESET_COLORS.map((color) => (
                  <button
                    key={color}
                    onClick={() => setNewDeptColor(color)}
                    className={`w-8 h-8 rounded-full transition-all ${
                      newDeptColor === color ? 'ring-2 ring-offset-2 ring-black scale-110' : ''
                    }`}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
              <div className="flex items-center gap-2 mt-3">
                <Input
                  type="color"
                  value={newDeptColor}
                  onChange={(e) => setNewDeptColor(e.target.value)}
                  className="w-12 h-10 p-1 cursor-pointer"
                />
                <Input
                  value={newDeptColor}
                  onChange={(e) => setNewDeptColor(e.target.value)}
                  placeholder="#e30613"
                  className="flex-1"
                />
              </div>
            </div>
            <div className="flex gap-3 justify-end pt-4">
              <Button variant="outline" onClick={() => setShowDeptDialog(false)}>
                Abbrechen
              </Button>
              <Button onClick={saveDepartment} className="bg-[#e30613] hover:bg-[#c00510]">
                Speichern
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminFeatures;
